import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Layers, Scissors, Shirt, Type, Zap, Coffee,
  CheckCircle, Clock, ArrowRight
} from "lucide-react";

const services = [
  {
    id: "impression-dtf",
    title: "Impression DTF",
    subtitle: "Transfert numérique",
    description: "L'impression DTF (Direct To Film) permet de transférer des visuels haute définition sur textiles. Cette technologie offre des couleurs vives, des détails précis et une excellente durabilité au lavage.",
    icon: Layers,
    color: "accent-green",
    applications: [
      "T-shirts personnalisés (tous coloris)",
      "Sacs et tote bags",
      "Polos et chemises",
      "Vêtements de travail",
      "Merchandising et goodies textiles"
    ],
    options: [
      "Impression couleur illimitée",
      "Détails fins et dégradés",
      "Résistant au lavage",
      "Toutes tailles de textiles"
    ],
    delay: "2-5 jours selon quantité",
    image: "/images/realisations/real-1.jpeg"
  },
  {
    id: "sublimation",
    title: "Sublimation",
    subtitle: "Tasses, gourdes, objets",
    description: "La sublimation intègre les visuels directement dans la matière. Idéale pour la personnalisation de mugs, gourdes et objets en céramique ou polyester avec une durabilité exceptionnelle.",
    icon: Coffee,
    color: "accent-red",
    applications: [
      "Tasses et mugs personnalisés",
      "Gourdes et bouteilles",
      "Objets promotionnels",
      "Cadeaux personnalisés",
      "Textiles polyester"
    ],
    options: [
      "Impression 360°",
      "Résistant lave-vaisselle",
      "Couleurs éclatantes",
      "Visuels photo HD"
    ],
    delay: "2-5 jours",
    image: "/images/realisations/real-4.jpeg"
  },
  {
    id: "serigraphie",
    title: "Sérigraphie",
    subtitle: "Grandes séries",
    description: "Technique professionnelle pour l'impression en série. La sérigraphie garantit des couleurs vives et durables, particulièrement adaptée aux commandes en volume.",
    icon: Shirt,
    color: "accent-yellow",
    applications: [
      "Séries textiles (50+ pièces)",
      "Affiches et posters",
      "Supports publicitaires",
      "Événementiel",
      "Packaging"
    ],
    options: [
      "Jusqu'à 6 couleurs",
      "Encres spéciales (fluo, paillettes)",
      "Haute durabilité",
      "Prix dégressifs en volume"
    ],
    delay: "5-10 jours selon série",
    image: "/images/realisations/real-10.jpeg"
  },
  {
    id: "flocage-flex",
    title: "Flocage & Flex",
    subtitle: "Marquage textile",
    description: "Le flocage offre un rendu velouté tandis que le flex permet des découpes précises. Idéal pour les noms, numéros et logos sur textiles sportifs ou professionnels.",
    icon: Type,
    color: "accent-green",
    applications: [
      "Maillots sportifs numérotés",
      "Noms et logos d'équipe",
      "Tenues de travail",
      "Casquettes personnalisées",
      "Vêtements promotionnels"
    ],
    options: [
      "Flocage velours",
      "Flex mat ou brillant",
      "Multi-couleurs",
      "Découpe de précision"
    ],
    delay: "2-5 jours",
    image: "/images/realisations/real-2.jpeg"
  },
  {
    id: "gravure-plaque",
    title: "Gravure & Plaques",
    subtitle: "Plexiglass & supports",
    description: "Création de plaques personnalisées et gravure de précision. Signalétique professionnelle, plaques commémoratives et décorations sur mesure.",
    icon: Zap,
    color: "accent-red",
    applications: [
      "Plaques signalétiques",
      "Plaques commémoratives",
      "Trophées et récompenses",
      "Décoration intérieure",
      "Signalétique de bureau"
    ],
    options: [
      "Plexiglass (transparent, noir, couleurs)",
      "Gravure laser",
      "Formats personnalisés",
      "Finitions premium"
    ],
    delay: "3-7 jours",
    image: "/images/realisations/real-3.jpeg"
  },
  {
    id: "infographie",
    title: "Infographie",
    subtitle: "Conception graphique",
    description: "Service complet de conception graphique. Création de visuels professionnels pour vos supports de communication : affiches, cartes de visite, kakémonos et plus.",
    icon: Scissors,
    color: "accent-yellow",
    applications: [
      "Cartes de visite",
      "Affiches publicitaires",
      "Kakémonos et roll-up",
      "Prospectus et flyers",
      "Calendriers et badges"
    ],
    options: [
      "Création graphique incluse",
      "Formats standards et sur mesure",
      "Impression haute qualité",
      "Conseil et accompagnement"
    ],
    delay: "3-7 jours",
    image: "/images/realisations/real-15.jpeg"
  }
];

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

export default function ServicesPage() {
  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section className="pt-32 pb-16 bg-secondary/30" data-testid="services-hero">
        <div className="container-custom">
          <motion.div {...fadeInUp}>
            <p className="eyebrow mb-3">Nos expertises</p>
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="services-title">
              Services
            </h1>
            <p className="text-muted text-lg max-w-2xl">
              Un atelier complet de sérigraphie, d'infographie et d'imprimerie pour répondre à tous 
              vos besoins. Impression sur tous supports : t-shirts, tasses, gourdes, casquettes, 
              plexiglass et plus encore.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services List */}
      <section className="section-padding" data-testid="services-list">
        <div className="container-custom space-y-24">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              className={`grid lg:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? "lg:flex-row-reverse" : ""
              }`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              data-testid={`service-detail-${service.id}`}
            >
              {/* Image */}
              <div className={`${index % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="aspect-[16/10] overflow-hidden bg-secondary">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>

              {/* Content */}
              <div className={`${index % 2 === 1 ? "lg:order-1" : ""}`}>
                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-12 h-12 flex items-center justify-center ${service.color}`}>
                    <service.icon size={24} className="text-background" />
                  </div>
                  <div>
                    <p className="font-mono text-xs text-muted">{service.subtitle}</p>
                    <h2 className="text-2xl md:text-3xl font-bold">{service.title}</h2>
                  </div>
                </div>

                <p className="text-muted mb-6">{service.description}</p>

                {/* Applications */}
                <div className="mb-6">
                  <h3 className="font-bold text-sm uppercase tracking-wider mb-3">Applications</h3>
                  <ul className="space-y-2">
                    {service.applications.map((app, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <CheckCircle size={16} className="text-[hsl(var(--accent-green))] flex-shrink-0 mt-0.5" />
                        <span>{app}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Options */}
                <div className="mb-6">
                  <h3 className="font-bold text-sm uppercase tracking-wider mb-3">Options disponibles</h3>
                  <div className="flex flex-wrap gap-2">
                    {service.options.map((opt, i) => (
                      <span key={i} className="equipment-badge text-xs">
                        {opt}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Delay & CTA */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <div className="flex items-center gap-2 text-sm text-muted">
                    <Clock size={16} />
                    <span>Délai indicatif: {service.delay}</span>
                  </div>
                  <Link 
                    to="/devis" 
                    className="btn-primary flex items-center gap-2"
                    data-testid={`service-cta-${service.id}`}
                  >
                    Demander un devis
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-foreground text-background" data-testid="services-cta">
        <div className="container-custom text-center">
          <motion.div {...fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Un projet spécifique ?
            </h2>
            <p className="text-background/70 mb-8 max-w-lg mx-auto">
              Chaque projet est unique. Contactez-nous pour discuter de vos besoins 
              et recevoir un devis personnalisé.
            </p>
            <Link 
              to="/devis" 
              className="btn-outline border-background text-background hover:bg-background hover:text-foreground"
              data-testid="services-final-cta"
            >
              Parlez-nous de votre projet
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
